/**
 * "+ Attach Existing": pick rows out of the library and attach them here.
 *
 * The counterpart to "+ Add New", and the one that makes the reuse tier usable. Without it the only
 * way to give a lab a section is to author a new one, which is how twelve labs that all bring up a
 * VPN end up with twelve copies of it - the exact drift the link tables exist to prevent. So the
 * default path for filling a lab is this dialogue, not the create form.
 *
 * Rows already attached here stay in the list, greyed, with the reason. Hiding them answers a
 * different question from the one somebody opened this to ask: "is the OSPF section already in this
 * lab" is worth an answer, and a row that is simply absent reads as one that does not exist.
 *
 * `choice` is for the tier where attaching needs a second decision. An item is attached to a
 * scenario *and a device* - that pairing is the attachment - so the picker collects both rather than
 * attaching to a default device and leaving somebody to correct it afterwards.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { HbrCheckbox } from '@harbor/elements-react-standalone';
import StbModal from '../security-toolbox-ui/StbModal';
import { LabOption } from '../../support/labService';
import { LabPanelState, labErrorMessage } from './LabShared';
import { LabSearchBox } from './LabAdminShared';

export type LabAttachChoice = {
  label: string;
  options: LabOption<string>[];
  /** Refuses the attach until every ticked row has answered. */
  required?: boolean;
  /** Preselected for every row. The commonest answer, when there is one. */
  defaultValue?: string;
  /** Said once above the list, since the same decision is being made for every row. */
  helpText?: string;
};

export type LabAttachPickerProps<T extends { id: number }> = {
  open: boolean;
  title: string;
  subtitle?: string;
  candidates: T[];
  loading?: boolean;
  error?: unknown;

  nameOf: (row: T) => React.ReactNode;
  search: (row: T, term: string) => boolean;
  /** One line under the name: what this row is. */
  summaryOf?: (row: T) => React.ReactNode;
  /** How far this row already reaches. The reason to attach a shared row rather than copy it. */
  usageOf?: (row: T) => React.ReactNode;

  /** Already attached here. Kept in the list and greyed, with `attachedReason` beside it. */
  attachedIds?: number[];
  attachedReason?: (row: T) => string;

  choice?: LabAttachChoice;

  submitLabel?: string;
  emptyMessage?: string;
  onClose: () => void;
  /** Resolves when the attach has been written. Errors are shown here, not thrown past. */
  onAttach: (picked: { row: T; choice: string }[]) => Promise<void>;
};

function LabAttachPicker<T extends { id: number }>({
  open,
  title,
  subtitle,
  candidates,
  loading,
  error,
  nameOf,
  search,
  summaryOf,
  usageOf,
  attachedIds = [],
  attachedReason,
  choice,
  submitLabel = 'Attach',
  emptyMessage = 'The library has nothing to attach here yet.',
  onClose,
  onAttach,
}: LabAttachPickerProps<T>) {
  const [term, setTerm] = useState('');
  const [ticked, setTicked] = useState<number[]>([]);
  const [choices, setChoices] = useState<Record<number, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setTerm('');
    setTicked([]);
    setChoices({});
    setFormError(null);
    setSaving(false);
  }, [open]);

  const attached = useMemo(() => new Set(attachedIds), [attachedIds]);

  const filtered = useMemo(() => {
    const needle = term.trim().toLowerCase();

    return needle ? candidates.filter((row) => search(row, needle)) : candidates;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, term]);

  const picked = useMemo(
    () => candidates.filter((row) => ticked.includes(row.id)),
    [candidates, ticked],
  );

  const toggle = (id: number) => {
    setFormError(null);
    setTicked((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    );

    if (choice?.defaultValue) {
      setChoices((current) => ({ ...current, [id]: current[id] ?? choice.defaultValue ?? '' }));
    }
  };

  const handleSubmit = async () => {
    if (!picked.length) {
      setFormError('Nothing is ticked yet.');
      return;
    }

    if (choice?.required) {
      const unanswered = picked.filter((row) => !choices[row.id]);

      if (unanswered.length) {
        setFormError(
          `${choice.label} is not set for ${unanswered.length} of the ${picked.length} ticked. It is part of the attachment, so there is no default to fall back on.`,
        );

        return;
      }
    }

    setFormError(null);
    setSaving(true);

    try {
      await onAttach(picked.map((row) => ({ row, choice: choices[row.id] || '' })));
      onClose();
    } catch (exc) {
      setFormError(labErrorMessage(exc, 'That could not be attached.'));
    } finally {
      setSaving(false);
    }
  };

  const narrowed = filtered.length !== candidates.length;

  return (
    <StbModal
      open={open}
      role="update"
      title={title}
      size="large"
      scrollable
      submitLabel={picked.length ? `${submitLabel} ${picked.length}` : submitLabel}
      submitDisabled={!picked.length || saving}
      suppressCloseOnSubmit
      onSubmit={handleSubmit}
      onClose={async () => onClose()}
      apiResponseSection={
        formError ? <p className="m-0 text-sm text-amber-800">{formError}</p> : undefined
      }
    >
      <div className="flex flex-col gap-3">
        {subtitle && <p className="m-0 text-sm text-gray-600">{subtitle}</p>}

        <LabSearchBox value={term} onChange={setTerm} placeholder="Search the library" />

        {choice?.helpText && <p className="m-0 text-xs text-gray-500">{choice.helpText}</p>}

        <LabPanelState
          loading={loading}
          error={error}
          empty={!filtered.length}
          emptyMessage={
            narrowed || term ? 'Nothing in the library matches that.' : emptyMessage
          }
          loadingLabel="Loading the library..."
        >
          <div className="max-h-80 overflow-y-auto rounded border border-gray-100">
            {filtered.map((row) => {
              const isAttached = attached.has(row.id);
              const isTicked = ticked.includes(row.id);

              return (
                <div
                  key={row.id}
                  className={`flex items-start gap-3 border-b border-gray-100 px-2 py-2 last:border-b-0 ${
                    isAttached ? 'bg-gray-50' : isTicked ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="pt-0.5">
                    <HbrCheckbox
                      size="small"
                      aria={{ label: 'Attach this one' }}
                      checked={isTicked}
                      disabled={isAttached}
                      onHbr-change={() => toggle(row.id)}
                    />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm">{nameOf(row)}</div>
                    {summaryOf && (
                      <div className="text-xs text-gray-500">{summaryOf(row)}</div>
                    )}
                    {usageOf && <div className="text-xs text-gray-500">{usageOf(row)}</div>}
                    {isAttached && (
                      <div className="text-xs text-amber-800">
                        {attachedReason?.(row) || 'Already attached here.'}
                      </div>
                    )}
                  </div>

                  {/* Only on the ticked rows: a select on every row in a long list is a wall of
                      controls, and answering it for something not being attached means nothing. */}
                  {choice && isTicked && (
                    <label className="flex flex-shrink-0 items-center gap-2 text-xs text-gray-600">
                      <span className="whitespace-nowrap">{choice.label}</span>
                      <select
                        className="rounded border border-gray-300 bg-white px-2 py-1 text-xs"
                        value={choices[row.id] || ''}
                        onChange={(evt) => {
                          setFormError(null);
                          setChoices((current) => ({ ...current, [row.id]: evt.target.value }));
                        }}
                      >
                        <option value="">Not set</option>
                        {choice.options.map((option) => (
                          <option value={option.value} key={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </div>
              );
            })}
          </div>
        </LabPanelState>

        <p className="m-0 text-xs text-gray-500">
          {picked.length
            ? `${picked.length} ticked. These are attached, not copied - editing one afterwards changes it everywhere it is used.`
            : 'Attaching links the library row here. It is not copied, so an edit to it reaches every other place it is used.'}
        </p>
      </div>
    </StbModal>
  );
}

export default LabAttachPicker;
